import { router } from "expo-router";
import { ChevronDown, ChevronRight } from "lucide-react-native";
import React, { useCallback, useEffect, useState } from "react";
import { Modal, Pressable, StyleSheet, Text, View } from "react-native";

type PublicMenuRoute =
  | "/list-your-business"
  | "/saved-businesses"
  | "/contact"
  | "/report-issue"
  | "/about"
  | "/terms"
  | "/privacy"
  | "/refund-policy"
  | "/listing-guidelines";

type PublicMenuLink = {
  label: string;
  pathname: PublicMenuRoute;
  carriesReturnTo?: boolean;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  returnTo?: string;
};

const TOP_MENU_ITEMS: PublicMenuLink[] = [
  {
    label: "List Your Business",
    pathname: "/list-your-business",
    carriesReturnTo: true,
  },
  {
    label: "About Us",
    pathname: "/about",
    carriesReturnTo: true,
  },
  {
    label: "Contact Us",
    pathname: "/contact",
    carriesReturnTo: true,
  },
  {
    label: "Report an Issue",
    pathname: "/report-issue",
    carriesReturnTo: true,
  },
  { label: "Saved Businesses", pathname: "/saved-businesses" },
];

const LEGAL_MENU_ITEMS: PublicMenuLink[] = [
  {
    label: "Terms of Use",
    pathname: "/terms",
    carriesReturnTo: true,
  },
  {
    label: "Privacy Policy",
    pathname: "/privacy",
    carriesReturnTo: true,
  },
  {
    label: "Refund Policy",
    pathname: "/refund-policy",
    carriesReturnTo: true,
  },
  {
    label: "Listing Guidelines",
    pathname: "/listing-guidelines",
    carriesReturnTo: true,
  },
];

export const PUBLIC_MENU_LABELS = [
  ...TOP_MENU_ITEMS.map((item) => item.label),
  "Legal",
  ...LEGAL_MENU_ITEMS.map((item) => item.label),
];

export default function PublicMenu({ visible, onClose, returnTo = "" }: Props) {
  const [legalExpanded, setLegalExpanded] = useState(false);

  useEffect(() => {
    if (visible) {
      setLegalExpanded(false);
    }
  }, [visible]);

  const openRoute = useCallback(
    (item: PublicMenuLink) => {
      onClose();

      if (item.carriesReturnTo && returnTo) {
        router.push({
          pathname: item.pathname,
          params: { returnTo },
        } as any);
        return;
      }

      router.push(item.pathname as any);
    },
    [onClose, returnTo]
  );

  const toggleLegal = useCallback(() => {
    setLegalExpanded((current) => !current);
  }, []);

  const LegalIcon = legalExpanded ? ChevronDown : ChevronRight;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.modalOverlay} onPress={onClose}>
        <Pressable style={styles.menuCard} onPress={() => {}}>
          <Text style={styles.menuTitle}>Menu</Text>

          {TOP_MENU_ITEMS.map((item) => (
            <Pressable
              key={item.pathname}
              onPress={() => openRoute(item)}
              style={styles.menuItem}
            >
              <Text style={styles.menuItemText}>{item.label}</Text>
            </Pressable>
          ))}

          <Pressable
            onPress={toggleLegal}
            accessibilityRole="button"
            accessibilityState={{ expanded: legalExpanded }}
            style={styles.menuItem}
          >
            <View style={styles.menuItemRow}>
              <Text style={styles.menuItemText}>Legal</Text>
              <LegalIcon color="#111827" size={18} strokeWidth={2.6} />
            </View>
          </Pressable>

          {legalExpanded
            ? LEGAL_MENU_ITEMS.map((item) => (
                <Pressable
                  key={item.pathname}
                  onPress={() => openRoute(item)}
                  style={[styles.menuItem, styles.legalMenuItem]}
                >
                  <Text style={styles.legalMenuItemText}>{item.label}</Text>
                </Pressable>
              ))
            : null}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.25)",
    padding: 16,
    justifyContent: "flex-start",
    alignItems: "flex-end",
  },
  menuCard: {
    width: 280,
    borderRadius: 16,
    backgroundColor: "#ffffff",
    padding: 12,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  menuTitle: {
    fontWeight: "900",
    marginBottom: 10,
    color: "#111827",
  },
  menuItem: {
    paddingVertical: 11,
    borderTopWidth: 1,
    borderTopColor: "#f1f5f9",
  },
  menuItemRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  menuItemText: {
    fontWeight: "800",
    color: "#111827",
  },
  legalMenuItem: {
    paddingLeft: 14,
  },
  legalMenuItemText: {
    fontWeight: "800",
    color: "#475569",
  },
});
